import React from "react";
import OpenAI, {Message} from "@semantical/services/openai.ts";
import Vocabulary from "@semantical/modules/vocabulary/Vocabulary.ts";
import { KnowledgeGraph } from "@semantical/modules/knowledge-graph";
import chatTypes from "@semantical/components/chat/chatTypes.ts?raw";
import {Advice, TVocabulary} from "./chatTypes.ts";


type Props = {
    onAdvice: (advice: Advice) => void
    vocabulary: Vocabulary
    model: KnowledgeGraph
}

type State = {
    messages: Message[]
}

type Intervals = {
    proposeConcepts: Interval
    proposeModel: Interval
}

type Interval = {
    timeout: NodeJS.Timeout | null,
    callback: () => void
}

export default class Chat extends React.Component<Props, State> {

    readonly intervals: Intervals = {
        proposeConcepts: {
            callback: () => (this.isQueryLoading() || this.proposeConcepts()),
            timeout: null
        },
        proposeModel: {
            callback: () => (this.isQueryLoading() || this.proposeModel()),
            timeout: null
        }
    }

    readonly chatbot = new OpenAI('You are a Data expert that give huge importance to semantic. Your only language to answer is through JSON. When a method is called, please consider parameters and provide answer as defined by the method definition.\n' + chatTypes);

    state: State = {
        messages: this.chatbot.messages.slice()
    }

    constructor(props : Props) {
        super(props);
    }

    render() {
        const roleClasses: Record<string, string> = {
            system: "text-accent-teal dark:text-cyan-400",
            user: "text-teal-700 dark:text-yellow-400",
            assistant: "text-slate-700 dark:text-white"
        };

        return (
            <div id="chat" className={
                "shrink-0 basis-[200px] p-5 overflow-y-auto border-t " +
                "bg-white text-slate-700 border-slate-200 shadow-[0_-2px_8px_rgba(100,120,140,0.06)] " +
                "dark:bg-[rgba(48,48,64,0.88)] dark:text-white dark:border-cyan-400 dark:shadow-none"
            }>
                <form onSubmit={(e) => this.doChatQuery(e)}>
                    <div className="flex items-center">
                        <input id="chatQueryInput" type={"text"} className={
                            "w-1/2 p-2.5 mt-2.5 border rounded-md " +
                            "bg-slate-100 text-slate-700 border-slate-200 " +
                            "focus:border-accent-teal focus:outline-none focus:ring-[3px] focus:ring-accent-teal/10 " +
                            "dark:bg-surface-dark-input dark:text-white dark:border-none dark:rounded-none " +
                            "dark:focus:ring-0"
                        }/>
                        <img id="chatQueryLoading" src="/src/assets/ripple.gif" alt="loading" width={40} height={40}
                             className="hidden ml-4"/>
                        <span><input type="checkbox"
                                     onChange={(e) => this.toggleInterval(e, this.intervals.proposeConcepts)}/>Propose Concepts</span>
                        <span><input type="checkbox"
                                     onChange={(e) => this.toggleInterval(e, this.intervals.proposeModel)}/>Propose Model</span>
                    </div>
                </form>
                {this.state.messages.slice().reverse().map((msg, i) => (
                    <div key={i} className={`mt-2.5 ${roleClasses[msg.role] || ""}`}>{msg.content}</div>
                ))}
            </div>
        )
    }

    toggleInterval = (e: React.ChangeEvent<HTMLInputElement>, interval: Interval) => {
        if (e.target.checked) {
            interval.timeout = setInterval(interval.callback, 10000);
            interval.callback();
        } else {
            if (interval.timeout) {
                clearInterval(interval.timeout);
                interval.timeout = null;
            }
        }
    }

    proposeModel() {
        const params = [this.props.model.toJSON(), 1];
        const query = 'AdviceService.getModelAdvice(' + params.map((p) => JSON.stringify(p)).join(",") + ')';
        this.showQueryLoading();
        this.chatbot.doChat(query, (response) => this.handleResponse(response));
    }

    proposeConcepts() {
        // build a string like "I'm within the Vehicle subject area of the Automobile Domain. The Subject Area contains the following Concepts: Vehicle, Car, Sedan, SUV, Electric Vehicle and Hybrid Car. What other concepts would you proposed to add to this subject area?"
        const subjectArea = "Ontology-Drive Conceptual Modelling";
        const domain = "Knowledge Management";
        const query = this.adviceService.getConceptAdvice(domain, subjectArea, this.props.vocabulary.toJSON(), 1);
        this.showQueryLoading();
        this.chatbot.doChat(query, (response) => this.handleResponse(response));
    }

    adviceService = {
        getConceptAdvice: (
            domain: string,
            subjectArea: string,
            vocabulary: TVocabulary,
            maxNumberOfConcepts: number
        ) => {
            const params = [domain, subjectArea, vocabulary, maxNumberOfConcepts]
            return 'AdviceService.getConceptAdvice(' + params.map((p) => JSON.stringify(p)).join(",") + ')';
        },
    }

    showQueryLoading() {
        const loading = document.getElementById("chatQueryLoading") as HTMLImageElement;
        loading.classList.remove("hidden");
    }

    isQueryLoading() {
        const loading = document.getElementById("chatQueryLoading") as HTMLImageElement;
        return !loading.classList.contains("hidden");
    }

    hideQueryLoading() {
        const loading = document.getElementById("chatQueryLoading") as HTMLImageElement;
        loading.classList.add("hidden");
    }

    doChatQuery(e : React.FormEvent<HTMLFormElement> ) {
        e.preventDefault();
        const input = document.getElementById("chatQueryInput") as HTMLInputElement;
        const query = input.value;
        input.value = "";
        this.showQueryLoading();
        this.chatbot.doChat(query, (response) => this.handleResponse(response));
    }

    handleResponse(response: string) {
        this.hideQueryLoading();
        console.log("chatbot response: " + response);
        this.props.onAdvice(extractJSONFromMarkdown(response) as Advice);
        this.setState({messages: this.chatbot.messages.slice()});
    }
}

function extractJSONFromMarkdown(mdContent: string): any {
    try {
        return JSON.parse(mdContent);
    } catch {
        // Regular expression to find JSON block inside triple backticks
        const regex = /```json\n([\s\S]*?)\n```/;
        const match = mdContent.match(regex);

        if (match && match[1]) {
            try {
                // Parse the JSON data found
                return JSON.parse(match[1]);
            } catch (error) {
                console.error("Failed to parse JSON:", error);
                return null;
            }
        } else {
            console.log("No JSON data found in the Markdown content.");
            return null;
        }
    }
}

export {Chat};